import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Building2, LayoutDashboard, FileText, Settings, TrendingUp, LogOut, ListTodo, BarChart3, Brain, Target, Leaf, Activity, Info } from 'lucide-react';
import { toast } from 'sonner';

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userStr = localStorage.getItem('ccip_user') || '{}';
  const user = JSON.parse(userStr);

  const handleLogout = () => {
    localStorage.removeItem('ccip_token');
    localStorage.removeItem('ccip_user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const baseNavItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/contracts', icon: FileText, label: 'Contracts' },
    { path: '/queue', icon: ListTodo, label: 'My Queue' },
    { path: '/market-rates', icon: TrendingUp, label: 'Market Rates' },
  ];

  // Add Executive Dashboard and analytics for executives, regional directors, and admins
  if (user && (user.role === 'EXECUTIVE' || user.role === 'REGIONAL_DIRECTOR' || user.role === 'ADMIN')) {
    // Insert Executive View after Dashboard (index 1)
    baseNavItems.splice(1, 0, { path: '/executive', icon: BarChart3, label: 'Executive View' });
    // Contracts is now at index 2 (right after Executive View)
    // Insert analytics after Contracts (index 3)
    baseNavItems.splice(3, 0, { path: '/predictions', icon: Brain, label: 'Predictions' });
    baseNavItems.splice(4, 0, { path: '/competitive', icon: Target, label: 'Competitive Intel' });
    baseNavItems.splice(5, 0, { path: '/esg', icon: Leaf, label: 'ESG & Carbon' });
    baseNavItems.splice(6, 0, { path: '/forecasting', icon: Activity, label: 'Load Forecast' });
  }

  const navItems = user && user.role === 'ADMIN' 
    ? [...baseNavItems, { path: '/admin', icon: Settings, label: 'Admin' }, { path: '/about', icon: Info, label: 'About' }]
    : [...baseNavItems, { path: '/about', icon: Info, label: 'About' }];

  return (
    <div className="flex h-screen bg-gray-50" data-testid="layout">
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#09409C] to-[#146CBB] flex items-center justify-center transform group-hover:scale-105 transition-transform">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-gradient-primary">PolarisIQ</span>
              </h1>
              <p className="text-[10px] text-gray-500 tracking-wide">Navigate Every Renewal</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-[#09409C] to-[#146CBB] text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-4 px-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#09409C] to-[#146CBB] flex items-center justify-center">
              <span className="text-white font-semibold">
                {user.first_name ? user.first_name[0] : 'U'}{user.last_name ? user.last_name[0] : 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user.first_name || 'User'} {user.last_name || ''}
              </p>
              <p className="text-xs text-gray-500 truncate">{user.role ? user.role.replace('_', ' ') : 'User'}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto" data-testid="main-content">
        {children}
      </main>

      {/* Powered by Resolvetech Badge - Covers platform badge */}
      <a 
        href="https://www.resolvetech.com" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-3 right-3 z-[10000] bg-white rounded-full shadow-lg px-3 py-1.5 flex items-center gap-2 border border-gray-200 hover:shadow-xl transition-shadow"
        style={{ zIndex: 10000 }}
        data-testid="resolvetech-badge"
      >
        <span className="text-xs text-gray-500 font-medium">Powered by</span>
        <img 
          src="https://customer-assets.emergentagent.com/job_energy-forecast-10/artifacts/9xwekntx_rtslogo-with-name-transparent-300-ppi-RGB.png" 
          alt="Resolvetech" 
          className="h-5 w-auto"
        />
      </a>
    </div>
  );
}

export default Layout;
